import React, { useLayoutEffect, useRef, useState } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import { ReactComponent as MenuIconWhite } from '../../../assets/icons/MenuDots.svg';
import { ReactComponent as MenuIconBlack } from '../../../assets/icons/MenuDotsB.svg';
import { ReactComponent as BinIcon } from '../../../assets/icons/TrashBin.svg';
import { ReactComponent as PenIcon } from '../../../assets/icons/Pen.svg';
import { ReactComponent as ListIcon } from '../../../assets/icons/list.svg';
import { ReactComponent as UserIcon } from '../../../assets/icons/User.svg';
import { ReactComponent as CalendarIcon } from '../../../assets/icons/Calendar-0.svg';
import { ReactComponent as DisplayIcon } from '../../../assets/icons/Display.svg';
import { ReactComponent as RefreshIcon } from '../../../assets/icons/Refresh.svg';
import './TableRowActionMenu.scss';

interface Option {
  key: string;
  value: string;
}

interface Props {
  options: Option[];
  id: string | any;
  onActionMenuClick: (id: string | any, action: string) => void;
  menuWidth?: number;
}

const getScrollParent = (node: HTMLElement | null): HTMLElement | null => {
  if (!node) return null;

  let parent = node.parentElement;
  while (parent) {
    const { overflowY } = window.getComputedStyle(parent);
    if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
      return parent;
    }
    parent = parent.parentElement;
  }

  return null;
};

const TableRowActionMenu = ({ options, id, onActionMenuClick, menuWidth }: Props) => {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [menuPlacement, setMenuPlacement] = useState<'top' | 'bottom'>('bottom');
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!isMenuOpen || !containerRef.current || !menuRef.current) {
      setMenuPlacement('bottom');
      return;
    }

    const containerRect = containerRef.current.getBoundingClientRect();
    const menuHeight = menuRef.current.offsetHeight;
    const scrollParent = getScrollParent(containerRef.current);
    const boundaryRect = scrollParent?.getBoundingClientRect() ?? {
      top: 0,
      bottom: window.innerHeight,
    };
    const spaceBelow = boundaryRect.bottom - containerRect.bottom;
    const spaceAbove = containerRect.top - boundaryRect.top;

    if (spaceBelow < menuHeight + 8 && spaceAbove > spaceBelow) {
      setMenuPlacement('top');
    } else {
      setMenuPlacement('bottom');
    }
  }, [isMenuOpen, options]);

  const actionMenuStyles = {
    backgroundColor: isMenuOpen ? '#238698' : 'transparent',
  };

  const onOptionClick = (
    e: React.MouseEvent<any>,
    id: string,
    option: Option
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setIsMenuOpen(false);
    onActionMenuClick(id, option.key);
  };

  const onMenuIconClick = (e: React.MouseEvent<any>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsMenuOpen((prev) => !prev);
  };
  return (
    <OutsideClickHandler onOutsideClick={() => setIsMenuOpen(false)}>
      <div className='action-menu-container' ref={containerRef}>
        <div className='menu-btn' style={actionMenuStyles}>
          {isMenuOpen ? (
            <MenuIconWhite onClick={(e) => onMenuIconClick(e)} />
          ) : (
            <MenuIconBlack onClick={(e) => onMenuIconClick(e)} />
          )}
        </div>
        {isMenuOpen && (
          <div
            ref={menuRef}
            className={`action-menu ${menuPlacement === 'top' ? 'action-menu--top' : ''}`}
            style={menuWidth ? { width: menuWidth, maxWidth: menuWidth } : undefined}
          >
            {options.map((option) => (
              <div
                onClick={(e) => onOptionClick(e, id, option)}
                key={option.key}
                className='option'
              >
                {option.key === 'edit' ? (
                  <PenIcon />
                ) : option.key === 'view' ? (
                  <ListIcon />
                ) : option.key === 'delete' ? (
                  <BinIcon />
                ) : option.key === 'inuvi-booking' ? (
                  <CalendarIcon />
                ) : option.key === 'toggle' ? (
                  <DisplayIcon />
                ) : option.key === 'generate_scores' ? (
                  <RefreshIcon />
                ) : (
                  <UserIcon />
                )}
                {option.value}
              </div>
            ))}
          </div>
        )}
      </div>
    </OutsideClickHandler>
  );
};

export default TableRowActionMenu;
